/* wb-search-invite.js — placeholder animé du champ de recherche du header
   (demande de Simon, 05/10/2026).
   Copie de référence du dépôt parent : l'original déployé est
   `assets/wb-search-invite.js` du thème live (en cas de doute le thème live fait foi).
   Chargé en `defer` par `snippets/predictive-search.liquid`.

   Le champ porte deux attributs rendus depuis la locale de la page :
     data-wb-invite        `general.search.placeholder_example`  « Je cherche %s »
     data-wb-invite-terms  `general.search.placeholder_examples` « une Fender Stratocaster|un ampli guitare|… »
   Chaque terme se tape lettre par lettre, reste affiché, s'efface ; le suivant arrive.
   Le premier est tiré au hasard : sans ça, un visiteur ne verrait que lui.

   On ne touche QUE l'attribut `placeholder` : aucun événement émis, aucun
   décalage de mise en page, et `wb-search.js` (chargé à l'intention de recherche)
   ne voit rien. Le HTML servi garde le texte fixe : sans JS, rien ne change.

   ⛔ `wb-search.js` écoute `focus` et `input` en CAPTURE sur `document` et appelle
   `stopPropagation()` : un écouteur posé sur le champ ne reçoit jamais le focus
   une fois le moteur chargé. On écoute donc en capture sur `window`, le premier
   nœud du trajet, que même un `stopImmediatePropagation` posé sur `document`
   n'atteindrait pas.

   Pas d'animation : visiteur en « réduire les animations », modèle sans `%s` ou
   moins de deux termes (clé absente de la locale). Pause : champ actif, champ
   rempli, onglet caché ; le texte fixe revient alors aussitôt. */
window.WBSearchInvite = (function () {
  'use strict';

  var MARKER = '%s';
  var SEPARATOR = '|';
  var CARET = '|';
  var MIN_TERMS = 2;
  var MAX_TERM_LENGTH = 40; // au-delà, le terme ne tient plus dans le champ mobile
  var ACTIVE_ATTR = 'data-wb-invite-on';
  var DELAYS = {
    start: 1500, // le texte fixe d'abord : la page se pose avant que ça bouge
    type: 80,
    hold: 2200,
    erase: 40,
    gap: 500,
  };

  function parseTerms(raw) {
    if (typeof raw !== 'string') return [];
    return raw.split(SEPARATOR)
      .map(function (term) { return term.trim(); })
      .filter(function (term, i, all) {
        return term && term.length <= MAX_TERM_LENGTH && all.indexOf(term) === i;
      });
  }

  function isTemplate(template) {
    return typeof template === 'string' && template.split(MARKER).length === 2;
  }

  /* Les étapes d'un terme : `text` à afficher, puis `delay` avant l'étape suivante.
     Ce qui suit `%s` dans le modèle (un guillemet fermant, s'il y en a un)
     n'apparaît qu'une fois le terme complet. */
  function frames(template, term) {
    var parts = template.split(MARKER);
    var letters = Array.from(term);
    var typing = function (count, delay) {
      return { text: parts[0] + letters.slice(0, count).join('') + CARET, delay: delay };
    };
    var typed = letters.map(function (_, i) { return typing(i + 1, DELAYS.type); });
    var erased = letters.map(function (_, i) {
      var count = letters.length - 1 - i;
      return typing(count, count === 0 ? DELAYS.gap : DELAYS.erase);
    });
    return typed
      .concat([{ text: parts[0] + term + parts[1], delay: DELAYS.hold }])
      .concat(erased);
  }

  function prefersReducedMotion(win) {
    return typeof win.matchMedia === 'function' &&
      win.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function attach(input, env) {
    var win = env.win;
    var doc = env.doc;
    var random = env.random || Math.random;
    var template = input.getAttribute('data-wb-invite');
    var terms = parseTerms(input.getAttribute('data-wb-invite-terms'));
    if (!isTemplate(template) || terms.length < MIN_TERMS || prefersReducedMotion(win)) {
      return null;
    }
    var original = input.getAttribute('placeholder') || '';
    var state = { next: Math.floor(random() * terms.length) % terms.length, cycle: [], position: 0 };
    var timer = null;

    function busy() {
      return Boolean(doc.hidden) || doc.activeElement === input || input.value !== '';
    }
    function pause() {
      if (timer !== null) win.clearTimeout(timer);
      timer = null;
      state = { next: state.next, cycle: [], position: 0 };
      input.setAttribute('placeholder', original);
    }
    /* L'état réel (focus, valeur) se relit à l'étape suivante : pendant un `blur`,
       certains navigateurs donnent encore le champ pour `activeElement`. */
    function resume(delay) {
      if (timer === null) timer = win.setTimeout(step, delay);
    }
    function step() {
      timer = null;
      if (busy()) {
        pause();
        return;
      }
      if (state.position >= state.cycle.length) {
        state = {
          next: (state.next + 1) % terms.length,
          cycle: frames(template, terms[state.next]),
          position: 0,
        };
      }
      var frame = state.cycle[state.position];
      state = { next: state.next, cycle: state.cycle, position: state.position + 1 };
      input.setAttribute('placeholder', frame.text);
      timer = win.setTimeout(step, frame.delay);
    }

    function onFocus(e) { if (e.target === input) pause(); }
    function onBlur(e) { if (e.target === input) resume(DELAYS.gap); }
    function onVisibility() {
      if (doc.hidden) pause();
      else resume(DELAYS.gap);
    }

    win.addEventListener('focus', onFocus, true);
    win.addEventListener('blur', onBlur, true);
    doc.addEventListener('visibilitychange', onVisibility);
    if (!busy()) resume(DELAYS.start);

    return {
      stop: function () {
        win.removeEventListener('focus', onFocus, true);
        win.removeEventListener('blur', onBlur, true);
        doc.removeEventListener('visibilitychange', onVisibility);
        pause();
      },
    };
  }

  /* Rejouable : un champ déjà pris en main est marqué et sauté. */
  function init(win, doc) {
    return Array.prototype.slice.call(doc.querySelectorAll('input[data-wb-invite]'))
      .filter(function (input) { return !input.hasAttribute(ACTIVE_ATTR); })
      .map(function (input) {
        input.setAttribute(ACTIVE_ATTR, '');
        return attach(input, { win: win, doc: doc });
      })
      .filter(Boolean);
  }

  return {
    DELAYS: DELAYS,
    MAX_TERM_LENGTH: MAX_TERM_LENGTH,
    parseTerms: parseTerms,
    isTemplate: isTemplate,
    frames: frames,
    attach: attach,
    init: init,
  };
})();

if (typeof document !== 'undefined') window.WBSearchInvite.init(window, document);
